import { useReducer } from "react";
import type { ChatMessage, Conversation, ConversationType } from "@/lib/chat-types";
import type { FundComplianceFileRef } from "@/lib/fund-compliance/api";

/* ── State ───────────────────────────────────────────────────────── */

export interface SendMessagePayload {
  content: string;
  mode: "qa" | "review";
  attachments?: FundComplianceFileRef[];
}

export interface QueuedMessage {
  chatId: string;
  payload: SendMessagePayload;
}

export interface PaginationCursor {
  nextBefore: string | null;
  hasMore: boolean;
  loading: boolean;
}

export interface ChatState {
  conversations: Conversation[];
  activeId: string | null;
  messagesByChat: Record<string, ChatMessage[]>;
  cursorsByChat: Record<string, PaginationCursor>;
  draftAttachmentsByChat: Record<string, FundComplianceFileRef[]>;
  error: string | null;
  queuedMessage: QueuedMessage | null;
  selectedSkillId: string;
}

const DEFAULT_SKILL_ID = "compliance-qa";

export const initialChatState: ChatState = {
  conversations: [],
  activeId: null,
  messagesByChat: {},
  cursorsByChat: {},
  draftAttachmentsByChat: {},
  error: null,
  queuedMessage: null,
  selectedSkillId: DEFAULT_SKILL_ID,
};

/* ── Actions ─────────────────────────────────────────────────────── */

export type ChatAction =
  | { type: "BOOTSTRAP_SUCCESS"; conversations: Conversation[]; skillId: string }
  | { type: "BOOTSTRAP_ERROR"; error: string }
  | { type: "SELECT_CHAT"; chatId: string | null }
  | { type: "WS_HISTORY_LOADED"; chatId: string; hasMore: boolean; nextBefore: string | null }
  | { type: "LOAD_MORE_START"; chatId: string }
  | { type: "LOAD_MORE_SUCCESS"; chatId: string; messages: ChatMessage[]; hasMore: boolean; nextBefore: string | null }
  | { type: "LOAD_MORE_ERROR"; chatId: string }
  | { type: "MESSAGES_UPDATED"; chatId: string; messages: ChatMessage[] }
  | { type: "CONVERSATION_CREATED"; conversation: Conversation }
  | { type: "DELETE_CHAT"; chatId: string }
  | { type: "QUEUE_MESSAGE"; chatId: string; payload: SendMessagePayload }
  | { type: "FLUSH_QUEUE" }
  | { type: "ADD_DRAFT_ATTACHMENT"; chatId: string; attachment: FundComplianceFileRef }
  | { type: "UPDATE_DRAFT_ATTACHMENT"; chatId: string; oldStorageId: string; newAttachment: FundComplianceFileRef }
  | { type: "REMOVE_DRAFT_ATTACHMENT"; chatId: string; storageId: string }
  | { type: "CLEAR_DRAFT_ATTACHMENTS"; chatId: string }
  | { type: "UPDATE_CONVERSATION_TYPE"; chatId: string; conversationType: ConversationType }
  | { type: "CLEAR_ERROR" }
  | { type: "MESSAGE_SENT"; chatId: string; mode: ConversationType };

function updateConversationMessageCount(
  conversations: Conversation[],
  chatId: string,
  messageCount: number
) {
  const index = conversations.findIndex((conversation) => conversation.id === chatId);
  if (index === -1) return conversations;

  const current = conversations[index];
  if (!current || current.messageCount === messageCount) return conversations;

  const next = [...conversations];
  next[index] = { ...current, messageCount };
  return next;
}

/* ── Reducer ─────────────────────────────────────────────────────── */

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "BOOTSTRAP_SUCCESS":
      return {
        ...state,
        conversations: action.conversations,
        selectedSkillId: action.skillId,
        // Do NOT auto-select first chat — user lands on the empty state
        error: null,
      };

    case "BOOTSTRAP_ERROR":
      return { ...state, error: action.error };

    case "SELECT_CHAT":
      return { ...state, activeId: action.chatId, error: null };

    case "WS_HISTORY_LOADED": {
      return {
        ...state,
        cursorsByChat: {
          ...state.cursorsByChat,
          [action.chatId]: {
            nextBefore: action.nextBefore,
            hasMore: action.hasMore,
            loading: false,
          },
        },
      };
    }

    case "LOAD_MORE_START": {
      const existing = state.cursorsByChat[action.chatId];
      return {
        ...state,
        cursorsByChat: {
          ...state.cursorsByChat,
          [action.chatId]: {
            nextBefore: existing?.nextBefore ?? null,
            hasMore: existing?.hasMore ?? false,
            loading: true,
          },
        },
      };
    }

    case "LOAD_MORE_SUCCESS": {
      const existingMessages = state.messagesByChat[action.chatId] ?? [];
      // Prepend older messages (dedupe by id just in case)
      const existingIds = new Set(existingMessages.map((m) => m.id));
      const newMessages = action.messages.filter((m) => !existingIds.has(m.id));
      const mergedMessages = [...newMessages, ...existingMessages].sort((a, b) => a.timestamp - b.timestamp);

      return {
        ...state,
        messagesByChat: { ...state.messagesByChat, [action.chatId]: mergedMessages },
        cursorsByChat: {
          ...state.cursorsByChat,
          [action.chatId]: {
            nextBefore: action.nextBefore,
            hasMore: action.hasMore,
            loading: false,
          },
        },
        conversations: updateConversationMessageCount(state.conversations, action.chatId, mergedMessages.length),
      };
    }

    case "LOAD_MORE_ERROR": {
      const existing = state.cursorsByChat[action.chatId];
      return {
        ...state,
        cursorsByChat: {
          ...state.cursorsByChat,
          [action.chatId]: {
            nextBefore: existing?.nextBefore ?? null,
            hasMore: existing?.hasMore ?? false,
            loading: false,
          },
        },
      };
    }

    case "MESSAGES_UPDATED":
      return {
        ...state,
        messagesByChat: { ...state.messagesByChat, [action.chatId]: action.messages },
        conversations: updateConversationMessageCount(state.conversations, action.chatId, action.messages.length),
      };

    case "CONVERSATION_CREATED":
      return {
        ...state,
        conversations: [action.conversation, ...state.conversations],
        messagesByChat: { ...state.messagesByChat, [action.conversation.id]: [] },
        activeId: action.conversation.id,
        error: null,
      };

    case "DELETE_CHAT": {
      const nextMessages = { ...state.messagesByChat };
      const nextCursors = { ...state.cursorsByChat };
      const nextDrafts = { ...state.draftAttachmentsByChat };
      delete nextMessages[action.chatId];
      delete nextCursors[action.chatId];
      delete nextDrafts[action.chatId];

      return {
        ...state,
        conversations: state.conversations.filter((conversation) => conversation.id !== action.chatId),
        messagesByChat: nextMessages,
        cursorsByChat: nextCursors,
        draftAttachmentsByChat: nextDrafts,
        activeId: state.activeId === action.chatId ? null : state.activeId,
        queuedMessage: state.queuedMessage?.chatId === action.chatId ? null : state.queuedMessage,
      };
    }

    case "QUEUE_MESSAGE":
      return { ...state, queuedMessage: { chatId: action.chatId, payload: action.payload } };

    case "FLUSH_QUEUE":
      return { ...state, queuedMessage: null };

    case "ADD_DRAFT_ATTACHMENT": {
      const chatId = action.chatId;
      return {
        ...state,
        draftAttachmentsByChat: {
          ...state.draftAttachmentsByChat,
          [chatId]: [...(state.draftAttachmentsByChat[chatId] ?? []), action.attachment],
        },
      };
    }

    case "UPDATE_DRAFT_ATTACHMENT": {
      const chatId = action.chatId;
      return {
        ...state,
        draftAttachmentsByChat: {
          ...state.draftAttachmentsByChat,
          [chatId]: (state.draftAttachmentsByChat[chatId] ?? []).map((a) =>
            a.storageId === action.oldStorageId ? action.newAttachment : a
          ),
        },
      };
    }

    case "REMOVE_DRAFT_ATTACHMENT": {
      const chatId = action.chatId;
      return {
        ...state,
        draftAttachmentsByChat: {
          ...state.draftAttachmentsByChat,
          [chatId]: (state.draftAttachmentsByChat[chatId] ?? []).filter(
            (a) => a.storageId !== action.storageId
          ),
        },
      };
    }

    case "CLEAR_DRAFT_ATTACHMENTS":
      return {
        ...state,
        draftAttachmentsByChat: { ...state.draftAttachmentsByChat, [action.chatId]: [] },
      };

    case "UPDATE_CONVERSATION_TYPE":
      return {
        ...state,
        conversations: state.conversations.map((c) =>
          c.id === action.chatId ? { ...c, type: action.conversationType } : c
        ),
      };

    case "CLEAR_ERROR":
      return { ...state, error: null };

    case "MESSAGE_SENT":
      return {
        ...state,
        conversations: state.conversations.map((c) =>
          c.id === action.chatId ? { ...c, type: action.mode } : c
        ),
        draftAttachmentsByChat: { ...state.draftAttachmentsByChat, [action.chatId]: [] },
      };

    default:
      return state;
  }
}

/* ── Hook ────────────────────────────────────────────────────────── */

export function useChatState() {
  return useReducer(chatReducer, initialChatState);
}
